import { customElement } from 'lit/decorators.js';
import {
  AskConfirmation,
  ConfirmationButtonType,
  getConfirmationButtons
} from '../../../../webmodule-common/other/ui/modal-confirmation';
import { cache } from '../../cache-impl/cache-registry';
import { FabricationBucket } from '../data/quote-fabrication-buckets';
import {
  loadQuoteFabricationBuckets,
  quoteFabricationIconsStyles,
  renderQuoteFabricationIcons
} from '../data/quote-fabrication-icons';
import { CSSResult, html, TemplateResult } from 'lit';
import { emptyGuid } from '../../../../webmodule-common/other/api/empty-guid';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { getQuoteLockBadge } from './quote-lock-badge';
import { getQuoteNumberFormatted, getQuoteStatus } from '../../../quotes/data/quote-helper-functions';
import { IStaleData } from '../../../../webmodule-common/other/stale-data';
import { ItemReference } from '../../../../webmodule-common/cache/definitions/cache-item-reference';
import { LangFranchiseeQuoteListView } from '../../../../localization/scripts/dealer-franchisee/quotes/views/franchisee-quote-list-view-lang';
import { moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { ProjectResourceLink } from '../../cache-impl/resource-link';
import { QuoteCacheData } from '../../cache-impl/cache-data';
import { QuoteSetSibling, ResultGetQuoteSummary, ViewQuoteSummary } from '../../../api/dealer-api-interface-quote';
import { QuoteSummaryTable, QuoteSummaryTableOptions } from '../../../quotes/views/quote-list-view';
import {
  buildQuoteStateContext,
  buildSiblingMap,
  computeQuoteRowCapabilities
} from '../capabilities/quote-row-capabilities';
import { buildQuoteRowActions, QuoteActionCard, QuoteRowAction } from '../capabilities/quote-row-actions';
import { QuoteActionDialog } from './quote-action-dialog';
import { getUserInformativeDisplayName } from '../../common/cache-tools';
import { QuoteStateActions } from '../helper/quote-state-change-helper';
import { resolveURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { resourceProject } from '../../../projects/ui/launcher';
import { staleIcon } from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import {
  WebmoduleChangeEvent,
  WebmoduleRadioGroup,
  WebModuleTable,
  WebModuleTableColumnDef
} from '../../../../webmodule-common/components/src/webmodule-components';
import { validId } from '../../../../webmodule-common/other/ui/string-helper-functions';
import { isCynclyStaff } from '../../../../webmodule-common/other/currentuser-claims';
import { fireQuickInformationToast, fireQuickErrorToast } from '../../../../webmodule-common/other/toast-away';
import { QuoteResetSupport } from '../data/quote-reset-support';
import { QuoteListPageFragments } from '../quote-list-page-fragment-constants';

export class QuoteFilterChangedEvent extends Event {
  static readonly eventName = 'quote-filter-changed';

  constructor() {
    super(QuoteFilterChangedEvent.eventName, {
      bubbles: true,
      composed: true
    });
  }
}
@customElement('wm-franchiseequotesummarytable')
export class FranchiseeQuoteSummaryTable extends QuoteSummaryTable {
  private projectCache = cache().project;
  private quoteCache = cache().quote;
  private linkCache = cache().projectResourceLink;
  private showAllQuotes = true;
  private count = 1;
  protected staleData: IStaleData;
  public get showAllQuotesFlag(): boolean {
    return this.showAllQuotes;
  }
  // Siblings for the rows currently on the page, indexed by quote-set id. Populated in getPreFetched
  // from the summary endpoint's sibling list, so ellipsisMenu can grey actions with no per-row load.
  private siblingMap = new Map<string, QuoteSetSibling[]>();

  // Fabrication progress for the rows currently on the page, indexed by quote id. Populated in
  // getPreFetched from one aggregate call, so the status column costs no per-row load.
  private fabricationBuckets = new Map<string, FabricationBucket[]>();

  constructor(options: QuoteSummaryTableOptions) {
    super(options);
    //this blocks loading of any data until the first time we want it to occur.
    this.staleData = options.stale ?? { exists: () => false };
    this.sortAscending = false;
  }

  isDataLoadDelayed() {
    return true;
  }

  override customStyles(): CSSResult {
    return quoteFabricationIconsStyles;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getColumns(): WebModuleTableColumnDef[] {
    // Column sizing moves from Bootstrap `colpx-*` / `colpxmax-*` classes (which
    // don't pierce the new table's shadow DOM) to the column's own width model:
    // fixed widths (had `colpxmax-*`) become `width`, the flexible title column
    // keeps a `minWidth` floor and grows to fill.
    const columns: WebModuleTableColumnDef[] = [];
    columns.push({
      title: () => LangFranchiseeQuoteListView.quoteNumberTitle(),
      fieldName: 'quoteNumber',
      sortable: true,
      isDefaultSortDirectionAscending: false,
      width: '130px',
      fade: true,
      displayValue: (_table: WebModuleTable, item: unknown) => {
        const quoteSummary = item as ViewQuoteSummary;
        return this.stale(quoteSummary)
          ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
            html`${staleIcon()}${this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary))}`
          : this.quoteLink(quoteSummary, getQuoteNumberFormatted(quoteSummary));
      }
    });

    columns.push(
      ...([
        {
          title: () => LangFranchiseeQuoteListView.quoteTitle(),
          fieldName: 'title',
          sortable: true,
          minWidth: '300px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.quoteLink(quoteSummary, quoteSummary.title);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.projectTitle(),
          fieldName: 'id',
          sortable: false,
          width: '270px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getProjectLink(quoteSummary.id);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.clientTitle(),
          fieldName: 'quoteCustomerId',
          sortable: false,
          width: '240px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getCustomerLink(quoteSummary.quoteCustomerId);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.authorTitle(),
          fieldName: 'assignedToUserId',
          sortable: false,
          width: '200px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.getUserDisplayValue(quoteSummary.assignedToUserId);
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.lastModifiedTitle(),
          fieldName: 'lastModifiedDate',
          sortable: false,
          width: '180px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            const dt = new Date(quoteSummary.lastModifiedDate ?? '');
            return html`${dt.toLocaleDateString()} ${dt.toLocaleTimeString()}`;
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.statusTitle(),
          fieldName: 'state',
          sortable: false,
          width: '130px',
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            // Two independent signals, and both belong here: the quote's own state (which shows Copying
            // on the DESTINATION of a copy) and its lock state (which shows Copying on the SOURCE).
            // Without the second, copying A badged the new copy and left A — the one that had actually
            // gone read-only — looking untouched.
            return html`${getQuoteStatus(quoteSummary.state, true)}${getQuoteLockBadge(quoteSummary.lockState)}
            ${renderQuoteFabricationIcons(this.fabricationBuckets.get(quoteSummary.id) ?? [])}`;
          }
        },
        {
          title: () => LangFranchiseeQuoteListView.netAmountTitle(),
          fieldName: 'calculatedNetTotal',
          sortable: false,
          align: 'right',
          width: '160px',
          fade: true,
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return moneyToTemplateResult(quoteSummary.calculatedNetTotal, quoteSummary.quoteCurrency);
          }
        },
        {
          title: html` <webmodule-icon library="fa" name="fas-bars"></webmodule-icon>`,
          fieldName: 'id',
          sortable: false,
          align: 'center',
          width: '50px',
          displayValue: (_table: WebModuleTable, item: unknown) => {
            const quoteSummary = item as ViewQuoteSummary;
            return this.ellipsisMenu(quoteSummary);
          }
        }
      ] satisfies WebModuleTableColumnDef[])
    );
    return columns;
  }

  stale(row: ViewQuoteSummary) {
    return this.staleData.exists(row.id);
  }

  override enableAdvancedFiltering(): boolean {
    return true;
  }

  override advancedFilterTemplate(): TemplateResult {
    this.count++;
    const createRadioButton = (text: string, input: boolean) => {
      return html` <webmodule-radio value="${input}">${text}</webmodule-radio>`;
    };

    return html`
      <webmodule-radio-group
        name="filtergroup${this.count.toString()}"
        class="quote-author-filter"
        value="${this.showAllQuotes}"
        inline
        size="small"
        @webmodule-change=${(e: WebmoduleChangeEvent) => this.onQuoteAuthorChanged(e)}
      >
        ${createRadioButton(LangFranchiseeQuoteListView.allQuotesRadio(), true)}
        ${createRadioButton(LangFranchiseeQuoteListView.myQuotesRadio(), false)}
      </webmodule-radio-group>
    `;
  }

  /**
   * ONE completion path for every row action that changes or removes a quote, so the delete and
   * state-change branches below cannot drift apart in what they refresh.
   *
   * Refreshing this table is not enough. The old ellipsis routed deletes through
   * `QuoteListView.deleteQuote`, which also refreshed the per-state tab counts and the page totals;
   * the dialog does not, so the host has to be told. It matters more now than it did then, because a
   * cancel moves the row to a DIFFERENT tab — leaving both the source and destination counts stale.
   */
  protected async afterRowActionCompleted(quoteSummary: ViewQuoteSummary) {
    await this.refreshData();
    await this.quoteRowActionCompletedEvent?.(quoteSummary);
  }

  /**
   * Dispatch a chosen row action. Takes the action VALUE rather than a menu event, so the same code
   * serves the dialog; the values are unchanged from the old menu items, which is why the locked
   * runner, pre-lock gate and supplier-online gate below are untouched by the move to a dialog.
   */
  async runRowAction(value: QuoteRowAction, quoteSummary: ViewQuoteSummary) {
    // Copy and reset keep their existing (non state-change) flows. The list is cross-project, so the
    // only copy mode it offers maps to the standalone copy event it already had.
    if (value === 'copyOutOfProject') {
      await this.copyQuoteItem(quoteSummary);
      return;
    }
    if (value === 'resetToActive') {
      await this.resetQuoteToActive(quoteSummary);
      return;
    }

    if (!(await QuoteStateActions.checkBeforeRun())) return;

    // Delete confirms in the pre-lock gate, then deletes via the manager loaded UNDER the lock (not a
    // second unlocked manager), and is NOT gated on supplier-online.
    if (value === 'delete') {
      await QuoteStateActions.runLockedQuoteAction(quoteSummary.id, quoteSummary.supplierId, {
        preLockGate: () =>
          AskConfirmation(
            // Name the quote. A generic "delete the Quote?" gives the user nothing to check against,
            // and one mis-clicked row in a long list is unrecoverable.
            LangFranchiseeQuoteListView.deleteQuoteConfirmation({
              quoteNumber: getQuoteNumberFormatted(quoteSummary),
              quoteTitle: quoteSummary.title ?? ''
            }),
            getConfirmationButtons()[ConfirmationButtonType.yesNo]
          ),
        duringLock: async qcm => ((await qcm.deleteQuote()) ? 'success' : 'failed'),
        afterLock: async () => {
          QuoteStateActions.showDeletedMessage();
          await this.afterRowActionCompleted(quoteSummary);
        }
      });
      return;
    }

    // Cancel / Accept / Reject go through the locked state-change flow.
    const targetState = QuoteStateActions.toTargetState(value);
    if (targetState !== null) {
      await QuoteStateActions.runLockedQuoteAction(quoteSummary.id, quoteSummary.supplierId, {
        requiresSupplierOnline: true,
        duringLock: qcm => QuoteStateActions.applyStateChange(qcm, targetState),
        afterLock: () => this.afterRowActionCompleted(quoteSummary)
      });
    }
  }

  async resetQuoteToActive(quoteSummary: ViewQuoteSummary) {
    const confirmed = await AskConfirmation(
      LangFranchiseeQuoteListView.resetToActiveConfirmation(),
      getConfirmationButtons()[ConfirmationButtonType.yesNo]
    );

    if (!confirmed) {
      return;
    }

    try {
      const result = await QuoteResetSupport.resetQuoteFromIssuePending(quoteSummary.id);
      if (result) {
        fireQuickInformationToast(LangFranchiseeQuoteListView.resetToActiveSubmitted(), 3000);
      } else {
        fireQuickErrorToast(LangFranchiseeQuoteListView.resetToActiveFailed(), 3000);
      }
    } catch {
      fireQuickErrorToast(LangFranchiseeQuoteListView.resetToActiveError(), 3000);
    }
  }

  /**
   * The row's 3-bar control. Opens the rich action dialog DIRECTLY — there is deliberately no popup
   * menu in between (Task 229184). A dropdown can only grey an item; the dialog names the quote in its
   * header, explains what each action does, and says WHY a blocked action is blocked.
   */
  ellipsisMenu(quoteSummary: ViewQuoteSummary): TemplateResult {
    return html`<webmodule-icon-button
      library="fa"
      name="fas-bars"
      @click=${async (e: Event) => {
        e.stopPropagation();
        await this.openActionDialog(quoteSummary);
      }}
    ></webmodule-icon-button>`;
  }

  /**
   * Build the cards from data already on the page (summary + siblings + cached supplier config) — no
   * quote load — then dispatch the chosen action through the SAME path the menu items used.
   */
  private async openActionDialog(quoteSummary: ViewQuoteSummary): Promise<void> {
    const initial = await this.buildActionCards(quoteSummary, this.siblingMap.get(quoteSummary.quoteSetId) ?? []);

    const chosen = await QuoteActionDialog.Execute(
      initial.cards,
      LangFranchiseeQuoteListView.actionDialogTitle({
        quoteNumber: getQuoteNumberFormatted(quoteSummary),
        quoteTitle: quoteSummary.title ?? ''
      }),
      initial.lockOwnerName,
      () => this.rereadActionCards(quoteSummary.id)
    );
    if (chosen) await this.runRowAction(chosen, quoteSummary);
  }

  /**
   * Re-read THIS quote's summary and rebuild its cards. Runs in the background behind the open dialog
   * (see QuoteActionDialog.Execute) because the row's cached lock state can be stale: an edit lock is
   * released with no event and expires on a TTL with nothing running to announce it, so a quote that is
   * actually free can still read as `Locked` and wrongly grey Copy.
   */
  private async rereadActionCards(
    quoteId: string
  ): Promise<{ cards: QuoteActionCard[]; lockOwnerName: string } | null> {
    this.quoteCache.flush([quoteId]);
    const fresh = await this.quoteCache.getData(quoteId);
    if (!fresh?.quoteSummary) return null;
    return await this.buildActionCards(fresh.quoteSummary, fresh.siblings ?? []);
  }

  /**
   * Build the cards from a summary + its siblings — no quote load. Shared by the immediate (cached)
   * open and the background re-read so the two can never compute different menus from the same data.
   */
  private async buildActionCards(
    quoteSummary: ViewQuoteSummary,
    siblings: QuoteSetSibling[]
  ): Promise<{ cards: QuoteActionCard[]; lockOwnerName: string }> {
    const allowSupplierReview = QuoteStateActions.resolveAllowSupplierReview(quoteSummary.supplierId);
    const context = buildQuoteStateContext(quoteSummary, siblings);
    const capabilities = computeQuoteRowCapabilities(context, {
      allowSupplierReview,
      lockState: quoteSummary.lockState
    });

    const cards = buildQuoteRowActions(quoteSummary, {
      capabilities,
      currentUserId: getCurrentUser()?.id ?? emptyGuid,
      allowSupplierReview,
      isStaff: isCynclyStaff(),
      onIssuedFragment: this.pageFragment === QuoteListPageFragments.ISSUED,
      // The list is cross-project and has a single copy event that produces a standalone quote, so the
      // in-project modes are not offered here. The project resource view offers all three.
      supportedCopyModes: ['copyOutOfProject']
    });

    // Only resolve the holder's name when a card will actually show it.
    const needsOwnerName = cards.some(c => c.reason === 'lockedByOther');
    const lockOwnerName = needsOwnerName ? await getUserInformativeDisplayName(quoteSummary.lockOwnerId) : '';
    return { cards, lockOwnerName };
  }

  async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: ViewQuoteSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();
    this.eventHandler(async () => {});
  }

  // The list tabs are a fixed-height region; let the table own the vertical scroll
  // so a second (tab) scrollbar does not appear beside it. The fill-height flex
  // chain for the wrapper is set up in dealer_ui.scss. (Pagination summary, page
  // size and rows-per-page selector come from the DataTableWrapper defaults.)
  override fillHeight(): boolean {
    return true;
  }

  protected getPreFetched(results: ResultGetQuoteSummary): Promise<void>[] {
    const cachePrefetch = super.getPreFetched(results);

    // Index this page's siblings for the capability engine, and prefetch each supplier's config once so
    // resolveAllowSupplierReview() is populated before any menu opens (no per-row fetch).
    this.siblingMap = buildSiblingMap(results.siblings ?? []);
    for (const supplierId of new Set(results.quoteSummary.results.map(q => q.supplierId))) {
      cachePrefetch.push(QuoteStateActions.loadSupplierConfig(supplierId));
    }

    cachePrefetch.push(
      loadQuoteFabricationBuckets(results.quoteSummary.results.map(q => q.id)).then(buckets => {
        this.fabricationBuckets = buckets;
      })
    );

    const updateFromSummaries:
      | ((items: ViewQuoteSummary[], siblings?: QuoteSetSibling[]) => Promise<ItemReference<QuoteCacheData>[]>)
      | undefined = (this.quoteCache as any).updateFromSummaries.bind(this.quoteCache);
    if (updateFromSummaries) {
      //this is the optimized efficient method — pass siblings so the project view keeps them (no wipe)
      const p = updateFromSummaries(results.quoteSummary.results, results.siblings ?? []).then(items => {
        const quoteIds = items.map(x => x.id);

        return this.linkCache.preFetch(quoteIds).then(() => {
          const projectIds: string[] = [];
          results.quoteSummary.results.forEach(summary => {
            const projectId = this.linkCache.getLocalData(summary.id)?.projectId ?? emptyGuid;
            if (validId(projectId)) projectIds.push(projectId);
          });
          return this.projectCache.preFetch(projectIds);
        });
      });

      cachePrefetch.push(p);
    } else {
      //less optimized fallback
      const quoteIds = results.quoteSummary.results.map(x => x.id);

      cachePrefetch.push(
        this.quoteCache
          .preFetch(quoteIds)
          .then(() => {
            return this.linkCache.getMany(quoteIds);
          })
          .then((data: ItemReference<ProjectResourceLink>[] | null) => {
            const projectIds = data?.map(x => x.data.projectId) ?? [];
            return this.projectCache.preFetch(projectIds);
          })
      );
    }
    return cachePrefetch;
  }

  protected async onQuoteAuthorChanged(e: WebmoduleChangeEvent) {
    const target = e.target as WebmoduleRadioGroup | null;

    if (target === null) return;

    const groupValue = target.value;

    if (groupValue === 'true') {
      this.showAllQuotes = true;
      this.assignedToUserId = null;
    } else {
      this.assignedToUserId = getCurrentUser()?.id ?? null;
      this.showAllQuotes = false;
    }

    await this.resetPageAndRefresh();
    this.requestUpdate();
    this.dispatchEvent(new QuoteFilterChangedEvent());
  }

  afterReload() {
    this.dataIsStale = false;
  }

  protected getProjectLink(id: string) {
    if (id == emptyGuid) return '';

    const link = this.linkCache.getLocalData(id);

    if (!link) return '';

    const project = this.projectCache.getLocal(link.projectId);

    return project
      ? html`<a class="project-link" href="${resolveURL(resourceProject, project.id)}" data-projectid="${project.id}"
          >${project.data.projectSummary.title}</a
        >`
      : '';
  }
}
